import { useMemo } from "react";
import { FolderOpen, HardHat, GitBranch } from "lucide-react";
import type { Tag, TagGroup, TagGroupColor } from "../components/filters/types";
import type { DocumentCategoryRow } from "@/types/documents";
import type { DesignDisciplineRow, DesignStageRow } from "@/stores/admin/repository";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { filterLeafCategories } from "../utils/leafCategories";

interface TagGroupDefinition {
  id: string;
  labelKey: "categories" | "designDisciplines" | "designStages";
  icon: typeof FolderOpen;
  color: TagGroupColor;
}

/**
 * Group definitions for Teach Franklin modal (excludes Projects).
 * Colors match the brand design:
 * - Categories: section1 (black)
 * - Design Disciplines: section3 (gray)
 * - Design Stages: section2 (tan/brown)
 *
 * Labels are resolved from i18n translations via labelKey.
 */
const TAG_GROUP_DEFINITIONS: TagGroupDefinition[] = [
  {
    id: "categories",
    labelKey: "categories",
    icon: FolderOpen,
    color: "section1",
  },
  {
    id: "disciplines",
    labelKey: "designDisciplines",
    icon: HardHat,
    color: "section3",
  },
  {
    id: "stages",
    labelKey: "designStages",
    icon: GitBranch,
    color: "section2",
  },
];

interface UseTeachFranklinTagGroupsProps {
  categories: DocumentCategoryRow[];
  disciplines: DesignDisciplineRow[];
  stages: DesignStageRow[];
}

/**
 * Maps every category ID to its canonical filter ID.
 * When categories share the same name (e.g. "Drawing Plan" exists as both
 * image type ID 8 and text type ID 15), all IDs map to the canonical
 * (lowest ID) filter ID: { 8 → "category-8", 15 → "category-8" }.
 */
export type CategoryIdMap = Map<number, string>;

/**
 * Convert categories to Tag format.
 * Filter ID format: `category-{id}` (using ID for reliable matching)
 *
 * Deduplicates by name - categories can have same name with different
 * category_type (image vs text) which is for backend classification only.
 * Users just see the name, so we keep one per unique name.
 *
 * Also returns a CategoryIdMap that maps ALL category IDs (including
 * duplicates) to the canonical filter ID for that name.
 */
function buildCategoryTags(
  categories: DocumentCategoryRow[],
  language: string
): {
  tags: Tag[];
  categoryIdMap: CategoryIdMap;
} {
  // Sort by ID first to ensure consistent canonical selection (lowest ID wins)
  const sorted = [...filterLeafCategories(categories)].sort((a, b) => a.id - b.id);

  // Dedup keys stay on category_name (English) so canonical IDs are stable
  // across languages; only the display label switches on language.
  const nameToCanonicalId = new Map<string, number>();
  const categoryIdMap: CategoryIdMap = new Map();

  for (const cat of sorted) {
    if (!nameToCanonicalId.has(cat.category_name)) {
      nameToCanonicalId.set(cat.category_name, cat.id);
    }
    const canonicalId = nameToCanonicalId.get(cat.category_name)!;
    categoryIdMap.set(cat.id, `category-${canonicalId}`);
  }

  // Build deduplicated tags using canonical IDs only
  const dedupedCategories = sorted.filter(
    (cat) => nameToCanonicalId.get(cat.category_name) === cat.id
  );

  const tags = dedupedCategories
    .map((cat) => ({
      id: `category-${cat.id}`,
      label: language === "et" ? cat.name_et?.trim() || cat.category_name : cat.category_name,
      count: 0,
      categoryId: cat.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return { tags, categoryIdMap };
}

/**
 * Convert disciplines to Tag format.
 * Filter ID format: `construction-{id}`
 */
function buildDisciplineTags(disciplines: DesignDisciplineRow[], language: string): Tag[] {
  return disciplines
    .map((disc) => {
      const row = disc as DesignDisciplineRow & { name_en?: string | null };
      const label =
        language === "en"
          ? row.name_en?.trim() || disc.description?.trim() || disc.name
          : disc.name;
      return {
        id: `construction-${disc.id}`,
        label,
        count: 0,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Convert stages to Tag format.
 * Filter ID format: `stage-{id}`
 */
function buildStageTags(stages: DesignStageRow[], language: string): Tag[] {
  return stages
    .map((stage) => {
      const row = stage as DesignStageRow & { name_en?: string | null };
      const label = language === "en" ? row.name_en?.trim() || stage.name : stage.name;
      return {
        id: `stage-${stage.id}`,
        label,
        count: 0,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

interface UseTeachFranklinTagGroupsReturn {
  tagGroups: TagGroup[];
  categoryIdMap: CategoryIdMap;
}

/**
 * Hook to convert Teach Franklin data into TagGroup format.
 * Uses the same format as the filter components for consistent UX.
 *
 * @returns tagGroups - Array of TagGroup objects (Categories, Disciplines, Stages)
 * @returns categoryIdMap - Maps every category ID to its canonical filter ID
 */
export function useTeachFranklinTagGroups({
  categories,
  disciplines,
  stages,
}: UseTeachFranklinTagGroupsProps): UseTeachFranklinTagGroupsReturn {
  const { language, t } = useLanguage();

  return useMemo(() => {
    const { tags: categoryTags, categoryIdMap } = buildCategoryTags(categories, language);
    const disciplineTags = buildDisciplineTags(disciplines, language);
    const stageTags = buildStageTags(stages, language);

    const tagsByGroupId: Record<string, Tag[]> = {
      categories: categoryTags,
      disciplines: disciplineTags,
      stages: stageTags,
    };

    const tagGroups = TAG_GROUP_DEFINITIONS.map((def) => ({
      id: def.id,
      label: t.documents.tagFilter[def.labelKey],
      icon: def.icon,
      color: def.color,
      tags: tagsByGroupId[def.id] || [],
    }));

    return { tagGroups, categoryIdMap };
  }, [categories, disciplines, stages, language, t]);
}

/**
 * Convert document's current tags to filter ID format.
 * Used to initialize the selection state.
 *
 * @param categoryIdMap - When provided, maps document category IDs to their
 *   canonical filter IDs (resolving duplicates). Without it, falls back to
 *   `category-{id}` which may not match the deduplicated tag options.
 */
export function documentTagsToFilterIds(
  document: {
    categories?: Array<{ id: number; name: string }>;
    design_disciplines?: Array<{ id: number; name: string }>;
    design_stages?: Array<{ id: number; name: string }>;
  },
  categoryIdMap?: CategoryIdMap
): string[] {
  const filterIds: string[] = [];

  // Categories: use canonical mapping to resolve duplicate-name categories
  document.categories?.forEach((cat) => {
    const filterId = categoryIdMap?.get(cat.id) ?? `category-${cat.id}`;
    // Avoid duplicates when multiple doc categories map to same canonical ID
    if (!filterIds.includes(filterId)) {
      filterIds.push(filterId);
    }
  });

  // Disciplines: construction-{id}
  document.design_disciplines?.forEach((disc) => {
    filterIds.push(`construction-${disc.id}`);
  });

  // Stages: stage-{id}
  document.design_stages?.forEach((stage) => {
    filterIds.push(`stage-${stage.id}`);
  });

  return filterIds;
}

/**
 * Extract database IDs from filter IDs for saving.
 * Returns separate arrays for categories, disciplines, and stages.
 *
 * @param originalCategoryIds - The document's original category IDs before editing.
 *   When a canonical filter ID matches an original ID's mapping, the original DB ID
 *   is preserved to avoid silently switching category_type variants.
 * @param categoryIdMap - The canonical mapping from buildCategoryTags().
 */
export function filterIdsToDbIds(
  filterIds: string[],
  originalCategoryIds?: number[],
  categoryIdMap?: CategoryIdMap
): {
  categoryIds: number[];
  disciplineIds: number[];
  stageIds: number[];
} {
  const categoryIds: number[] = [];
  const disciplineIds: number[] = [];
  const stageIds: number[] = [];

  filterIds.forEach((filterId) => {
    if (filterId.startsWith("category-")) {
      const canonicalId = parseInt(filterId.replace("category-", ""), 10);
      if (isNaN(canonicalId)) return;

      // If we have the mapping and original IDs, try to preserve the original
      // DB ID when the document already had a category with the same name
      if (originalCategoryIds && categoryIdMap) {
        const originalMatch = originalCategoryIds.find(
          (origId) => categoryIdMap.get(origId) === filterId
        );
        categoryIds.push(originalMatch ?? canonicalId);
      } else {
        categoryIds.push(canonicalId);
      }
    } else if (filterId.startsWith("construction-")) {
      const id = parseInt(filterId.replace("construction-", ""), 10);
      if (!isNaN(id)) disciplineIds.push(id);
    } else if (filterId.startsWith("stage-")) {
      const id = parseInt(filterId.replace("stage-", ""), 10);
      if (!isNaN(id)) stageIds.push(id);
    }
  });

  return { categoryIds, disciplineIds, stageIds };
}
